import { ReactElement, useEffect, useState, useMemo } from 'react'
import dayjs from 'dayjs'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'

import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'

import { CloseIcon } from 'src/v3/core/icons'
import { AssignmentCreditAuditLog } from 'src/v3/core/types'
import axios from 'src/utilities/http-client'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import { largeModalSx } from 'src/v3/manage/styles'
import {
  DATAGRID_DEFAULT_PAGE_SIZE,
  DATAGRID_PAGE_SIZE_OPTIONS,
} from 'src/v3/manage/constants'

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
  userID?: string
  title?: string
}

type AuditLogRow = AssignmentCreditAuditLog & { id: string }

let getActionDescription = (action: string): string => {
  switch (action) {
    case 'ManagerCompleteAssignment':
      return 'Completed Assignment'
    case 'ManagerCompleteAssignmentCourses':
      return 'Completed Assignment Courses'
    case 'ManagerCompleteAssignmentWithinThreshold':
      return 'Completed Assignment (Threshold)'
    default:
      return action
  }
}

let CustomToolbar = () => (
  <DataGridToolbar fileName="assignment-credit-audit-logs" />
)

let AuditLogModal = ({
  open,
  setOpen,
  userID,
  title = 'Assignment Credit Audit Logs',
}: Props): ReactElement => {
  let [logs, setLogs] = useState<AssignmentCreditAuditLog[]>([])
  let [loading, setLoading] = useState(false)
  let [error, setError] = useState<string>('')

  useEffect(() => {
    if (open) {
      setLoading(true)
      setError('')

      let endpoint = userID
        ? `/api/manage/audit/user/${userID}/assignment-credit`
        : '/api/manage/audit/assignment-credit'

      axios
        .get<AssignmentCreditAuditLog[]>(endpoint)
        .then((response) => {
          setLogs(response.data)
        })
        .catch((err) => {
          setError(err.response?.data?.message || 'Failed to load audit logs')
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [open, userID])

  let rows: AuditLogRow[] = useMemo(
    () =>
      logs.map((log) => ({
        ...log,
        id: log.id,
      })),
    [logs],
  )

  let columns: GridColDef[] = [
    {
      field: 'createdTime',
      headerName: 'Date',
      width: 180,
      type: 'dateTime',
      sortable: true,
      renderCell: (params) => (
        <span>{dayjs(params.value).format('MMM DD, YYYY h:mm A')}</span>
      ),
      valueGetter: (params) => new Date(params),
    },
    {
      field: 'actorUserName',
      headerName: 'Manager',
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: 'targetUserName',
      headerName: 'Student',
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 220,
      sortable: true,
      renderCell: (params) => (
        <Chip
          size="small"
          label={getActionDescription(params.value)}
          variant="outlined"
          sx={{
            backgroundColor: 'transparent',
            borderColor: 'divider',
            color: 'text.secondary',
          }}
        />
      ),
    },
    {
      field: 'assignmentName',
      headerName: 'Assignment',
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
      field: 'courseNames',
      headerName: 'Courses',
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => {
        let courseNames = params.row.courseNames as string[]
        if (!courseNames || courseNames.length === 0) {
          return <em>All courses</em>
        }
        return (
          <Box>
            {courseNames.map((courseName, idx) => (
              <div key={idx}>{courseName}</div>
            ))}
          </Box>
        )
      },
    },
  ]

  return (
    <Dialog
      onClose={() => setOpen(false)}
      open={open}
      aria-labelledby="audit-log-dialog-title"
      slotProps={largeModalSx}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <DialogTitle
          id="audit-log-dialog-title"
          sx={{ textAlign: 'center', pb: 2 }}
        >
          {title}
        </DialogTitle>
        <DialogContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            p: 3,
            pt: 0,
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && logs.length === 0 && (
            <Box
              sx={{
                height: 400,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                color: 'text.secondary',
                backgroundColor: 'background.paper',
                borderRadius: 1,
              }}
              role="status"
              aria-live="polite"
            >
              <Box
                sx={{ fontSize: 64, mb: 2, opacity: 0.5 }}
                role="img"
                aria-label="No audit logs icon"
              >
                📋
              </Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                No Audit Logs Found
              </Typography>
              <Typography variant="body2">
                No assignment credit audit logs found for the selected criteria.
              </Typography>
            </Box>
          )}

          {(loading || logs.length > 0) && (
            <Box sx={{ flex: 1, width: '100%', minHeight: 400 }}>
              <Typography id="audit-logs-table-description" className="sr-only">
                Table showing audit logs of assignments completed by manager
                intervention. Use arrow keys to navigate, Enter to select rows,
                Space to toggle selection.
              </Typography>
              <DataGridPremium
                rows={rows}
                columns={columns}
                slots={{ toolbar: CustomToolbar }}
                showToolbar
                loading={loading}
                aria-label="Assignment credit audit logs table"
                aria-describedby="audit-logs-table-description"
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: DATAGRID_DEFAULT_PAGE_SIZE },
                  },
                  sorting: {
                    sortModel: [{ field: 'createdTime', sort: 'desc' }],
                  },
                }}
                pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
                disableRowSelectionOnClick
                autoHeight
                sx={{
                  backgroundColor: 'background.paper',
                  border: 1,
                  borderRadius: 1,
                  borderColor: 'divider',
                  '& .MuiDataGrid-cell': {
                    borderColor: 'divider',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: 'background.paperDark',
                    borderColor: 'divider',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: 'background.paperDark',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderColor: 'divider',
                    backgroundColor: 'background.paperDark',
                  },
                  '& .MuiDataGrid-row': {
                    '&:hover': {
                      backgroundColor: 'background.paperDark',
                    },
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'center',
            p: 3,
            pt: 2,
            backgroundColor: 'background.paper',
          }}
        >
          <Button
            onClick={() => setOpen(false)}
            color="primary"
            variant="contained"
            startIcon={<CloseIcon />}
            aria-label="Close"
          >
            Close
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

export default AuditLogModal
